import React from 'react';
import { Form } from 'mobx-react-form';
import { observable } from 'mobx';
import validatorjs from 'validatorjs';
import dvr from 'mobx-react-form/lib/validators/DVR';
import {
    validationExtension,
    rvalueRules,
    percentageRule,
    formInvalidMessage
} from '../_shared/validation/validationhelpers';
import ceil from 'lodash.ceil';

export default class ExamplesForm extends Form {
    URLSegment = '';

    store = null;

    @observable numItems = 0;

    setURL(url) {
        this.URLSegment = url;
    }

    setStore(store) {
        this.store = store;
    }

    plugins() {
        return {
            dvr: dvr({
                package: validatorjs,
                extend: validationExtension
            })
        };
    }

    options() {
        return {
            validateOnInit: false,
            validateOnChange: true,
            showErrors: true
        };
    }

    getDescription(content) {
        return <div dangerouslySetInnerHTML={{ __html: content.join('') }} />;
    }

    setup() {
        return {
            fields: [
                {
                    name: 'section5',
                    type: 'formsection',
                    label: 'Area (calculated) field',
                    fields: [
                        {
                            name: 'example-11',
                            type: 'areafield',
                            label: 'How big is the wall?',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand the size of the surface heat can escape through.</p>'
                                    ]);
                                }
                            },
                            fields: [
                                {
                                    name: 'example-11-length',
                                    label: 'length',
                                    type: 'numberfield',
                                    extra: { unit: 'm' },
                                    value: null,
                                    placeholder: 'Length',
                                    rules: 'required|numeric'
                                },
                                {
                                    name: 'example-11-height',
                                    label: 'height',
                                    type: 'numberfield',
                                    extra: { unit: 'm' },
                                    value: null,
                                    placeholder: 'Height',
                                    rules: 'required'
                                },
                                {
                                    name: 'example-11-result',
                                    label: 'result',
                                    type: 'numberfield',
                                    extra: { unit: 'm2' },
                                    value: 0,
                                    placeholder: 'Result',
                                    rules: 'required|numeric|min:0'
                                }
                            ]
                        },
                        {
                            name: 'example-12-test',
                            type: 'numberfield',
                            label: 'Thing',
                            extra: { unit: 'm' },
                            value: 0,
                            placeholder: 'Length',
                            rules: 'required|numeric|min:90'
                        }
                    ]
                },
                {
                    name: 'section0',
                    type: 'formsection',
                    label: 'Add fields',
                    extra: {},
                    fields: [
                        {
                            name: 'count',
                            type: 'stepper',
                            value: 0,
                            label: 'How many items?',
                            rules: 'required|between:0,10',
                            extra: {
                                min: 0,
                                max: 10,
                                onChange: (value) => {
                                    this.addItem(value);
                                }
                            }
                        },
                        {
                            name: 'items',
                            extra: {},
                            value: '',
                            fields: [],
                            type: 'collection'
                        }
                    ]
                },
                {
                    name: 'section1r',
                    type: 'formsection',
                    label: 'Radio/rvalue fields',
                    fields: [
                        {
                            name: 'example-r1',
                            type: 'radiorvaluefield',
                            label: null,
                            extra: {},
                            fields: [
                                {
                                    name: 'example-r1a',
                                    type: 'radio',
                                    value: 'single',
                                    label:
                                        '1. What type of glazing does this window have?',
                                    rules: rvalueRules.glazing,
                                    extra: {
                                        options: () => {
                                            return [
                                                { value: 'single', label: 'Single glazing' },
                                                { value: 'double', label: 'Double glazing or better' }
                                            ];
                                        }
                                    }
                                },
                                {
                                    name: 'example-r1b',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value',
                                    value: null,
                                    rules: rvalueRules.glazing,
                                    extra: {
                                        era: 'Before 1978',
                                        zone: 1,
                                        element: 'glazing',
                                        glazing: 'single',
                                        showHint: false,
                                        showIf: {
                                            '===': [{ var: 'example-r1a' }, 'single']
                                        }
                                    }
                                },
                                {
                                    name: 'example-r1c',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value (Double)',
                                    value: null,
                                    rules: rvalueRules.glazing,
                                    extra: {
                                        era: 'Before 1978',
                                        zone: 1,
                                        element: 'glazing',
                                        glazing: 'double',
                                        showHint: false,
                                        showIf: {
                                            '===': [{ var: 'example-r1a' }, 'double']
                                        }
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    name: 'section1p',
                    type: 'formsection',
                    label: 'Percent/rvalue fields',
                    fields: [
                        {
                            name: 'example-p1',
                            type: 'percentrvaluefield',
                            label: null,
                            extra: {},
                            fields: [
                                {
                                    name: 'example-p1a',
                                    type: 'numberfield',
                                    label:
                                        '1. Percentage / R-value combo field',
                                    value: 90,
                                    rules: percentageRule,
                                    extra: {
                                        unit: '%',
                                        description: () => {
                                            return this.getDescription([
                                                '<p>The second percentage field is linked to the first to show the remainder.</p>'
                                            ]);
                                        }
                                    },
                                    observers: [
                                        {
                                            key: 'value', // can be any prop
                                            call: ({ form, field, change }) => {
                                                // if this field is invalid dont do anything
                                                if (
                                                    change.newValue > 100 ||
                                                    change.newValue < 0
                                                ) { return; }

                                                const remaining = ceil(
                                                    100 - change.newValue,
                                                    2
                                                );

                                                form.$(
                                                    'section1p.example-p2.example-p2a'
                                                ).set('value', remaining);
                                            }
                                        }
                                    ]
                                },
                                {
                                    name: 'example-p1b',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value ',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: 'Before 1978',
                                        zone: 1,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'standard',
                                        showHint: false
                                    }
                                }
                            ]
                        },
                        {
                            name: 'example-p2',
                            type: 'percentrvaluefield',
                            label: null,
                            extra: {},
                            fields: [
                                {
                                    name: 'example-p2a',
                                    type: 'numberfield',
                                    label: '3. Remainder percentage',
                                    value: 10,
                                    disabled: true,
                                    extra: {
                                        unit: '%'
                                    }
                                },
                                {
                                    name: 'example-p2b',
                                    type: 'rvaluefield',
                                    label: 'Estimated R-value ',
                                    value: null,
                                    rules: rvalueRules.floor,
                                    extra: {
                                        era: 'Before 1978',
                                        zone: 1,
                                        element: 'floor',
                                        surface: 'external',
                                        standard: 'nonstandard',
                                        showHint: false
                                    }
                                }
                            ]
                        }
                    ]
                },
                {
                    name: 'section2',
                    type: 'formsection',
                    label: 'Number fields',
                    fields: [
                        {
                            name: 'example-5',
                            type: 'numberfield',
                            label: '5. Number field (m)',
                            value: 1.25,
                            rules: 'required|numeric',
                            extra: {
                                unit: 'm'
                            }
                        },
                        {
                            name: 'example-6',
                            type: 'numberfield',
                            label: '6. Number field (m²)',
                            value: 678.34,
                            rules: 'required|numeric',
                            extra: {
                                unit: 'm2',
                                description: () => {
                                    return this.getDescription([
                                        '<p>In tempus condimentum sapien eu auctor. Integer sodales elementum tempor. Nullam fermentum sem quis lobortis volutpat. In pellentesque tortor sit amet magna tempus.</p>'
                                    ]);
                                }
                            }
                        },
                        {
                            name: 'example-7',
                            type: 'numberfield',
                            label: '7. Number field (%)',
                            value: 50,
                            rules: 'required|numeric|min:0|max:100',
                            extra: {
                                unit: '%'
                            }
                        }
                    ]
                },
                {
                    name: 'section3',
                    type: 'formsection',
                    label: 'Radio fields',
                    fields: [
                        {
                            name: 'example-8',
                            type: 'radio',
                            value: 'Yes',
                            label: '8. Radio group vertical',
                            rules: 'required',
                            extra: {
                                options: () => {
                                    return [
                                        { value: 'Yes', label: 'Yes' },
                                        { value: 'No', label: 'No' },
                                        { value: 'Maybe', label: 'Maybe' },
                                        {
                                            value: 'Long',
                                            label:
                                                'Curabitur nec quam non nunc tristique auctor sit amet dictum tortor. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia Curae'
                                        }
                                    ];
                                },
                                description: () => {
                                    return this.getDescription([
                                        '<p>This helps us understand if a heater needs to heat any extra space.</p>'
                                    ]);
                                }
                            }
                        },
                        {
                            name: 'example-9',
                            type: 'radio',
                            value: 'Yes',
                            label: '9. Radio group horizontal',
                            rules: 'required',
                            extra: {
                                inline: true,
                                options: () => {
                                    return [
                                        { value: 'Yes', label: 'Yes' },
                                        { value: 'No', label: 'No' }
                                    ];
                                }
                            }
                        }
                    ]
                },
                {
                    name: 'section4',
                    type: 'formsection',
                    label: 'R-value fields',
                    fields: [
                        {
                            name: 'example-10',
                            type: 'rvaluefield',
                            label: '10. Estimated R-value ',
                            value: null,
                            rules: 'required|numeric|min:0|max:32',
                            extra: {
                                era: 'Before 1978',
                                zone: 1,
                                element: 'floor',
                                surface: 'external',
                                standard: 'nonstandard',
                                showHint: false
                            }
                        }
                    ]
                },
                {
                    name: 'section6',
                    type: 'formsection',
                    label: 'Stepper field',
                    fields: [
                        {
                            name: 'example-12',
                            type: 'stepper',
                            value: 0,
                            label:
                                '12. How many marshmallows can you fit in your mouth?',
                            rules: 'required|between:0,10',
                            extra: {
                                min: 0,
                                max: 10,
                                description: () => {
                                    return this.getDescription([
                                        '<p>Mauris sodales ligula purus, nec efficitur felis commodo nec. Donec dignissim nibh quis maximus vulputate. Aliquam porta hendrerit quam, a condimentum odio imperdiet at.</p>'
                                    ]);
                                }
                            }
                        }
                    ]
                },
                {
                    name: 'section7',
                    type: 'formsection',
                    label: 'Select field',
                    fields: [
                        {
                            name: 'example-13',
                            type: 'select',
                            label: '13. Select your option',
                            rules: 'required',
                            placeholder: 'please select',
                            extra: {
                                options: () => {
                                    return [
                                        'Before 1978',
                                        'From 2001 to 2008',
                                        'From 2001 to 2007',
                                        'From 2009 to 2 Nov 2022'
                                    ];
                                }
                            }
                        }
                    ]
                },
                {
                    name: 'section8',
                    type: 'formsection',
                    label: 'Text field',
                    fields: [
                        {
                            name: 'example-14',
                            type: 'textfield',
                            label: '14. Enter your name',
                            rules: 'required|between:0,10',
                            extra: {
                                description: () => {
                                    return this.getDescription([
                                        '<p>This has a max length validation rule.</p>',
                                        '<p>Note the data icon issue still exists. Perhaps we can add an <a href="https://reactjs.org/blog/2017/07/26/error-handling-in-react-16.html">error boundary</a></p>'
                                    ]);
                                }
                            }
                        }
                    ]
                }
            ]
        };
    }

    addItem(value) {
        if (value < this.numItems && this.numItems > 0) {
            this.$('section0.items').del(`item-${this.numItems}`);
            this.numItems--;
        } else {
            const key = this.numItems + 1;
            this.$('section0.items').add(
                {
                    name: `item-${key}`,
                    type: 'fieldset',
                    extra: {},
                    fields: [
                        {
                            name: `items-${key}-name`,
                            label: `name ${key}`,
                            type: 'textfield',
                            value: '',
                            extra: {},
                            rules: 'required'
                        },
                        {
                            name: `items-${key}-height`,
                            label: `height ${key}`,
                            type: 'textfield',
                            value: '',
                            extra: {},
                            rules: 'required'
                        }
                    ]
                }
            );
            this.numItems = key;
        }
    }

    /**
     * Event Hooks
     */
    hooks() {
        return {
            onInit() {
                console.log('onInit', this.store);
            },
            onSubmit(form) {
                console.log('-> onSubmit HOOK - isValid?', form.isValid);
            },
            onSuccess(form) {
                const result = form.values();
                console.log('-> onSuccess HOOK - ', result);
            },
            onError(form) {
                form.invalidate(formInvalidMessage);
            }
        };
    }
}
